import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { mainNavigation } from "@/data/navigation";
import { Badge } from "@/components/ui/badge";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";

export const HeaderNavigation = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { hasRole, isAuthenticated } = useAuth();
  const isEmployer = isAuthenticated && hasRole('EMPLOYER') && !hasRole('ADMIN');

  const renderMenuItem = (item: any, isGridItem: boolean = false) => (
    <NavigationMenuLink asChild key={item.href}>
      <Link
        to={item.href}
        className={`block hover:bg-muted rounded-md text-foreground transition-colors ${isGridItem ? "p-3" : "p-3"}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            {item.icon && <span className={`${isGridItem ? "text-base mt-0.5" : "text-lg"}`}>{item.icon}</span>}
            <div className="flex-1">
              <span className={`font-medium ${isGridItem ? "text-sm" : ""}`}>{item.label}</span>
              {item.description && !isGridItem && (
                <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-shrink-0">
            {item.badge && (
              <Badge variant="secondary" className="text-xs">
                {item.badge}
              </Badge>
            )}
          </div>
        </div>
      </Link>
    </NavigationMenuLink>
  );

  return (
    <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList>
        {mainNavigation.map((section) => (
          <NavigationMenuItem key={section.label}>
            {section.items ? (
              <>
                <NavigationMenuTrigger className="bg-transparent text-white hover:bg-secondary-foreground/10 hover:text-white data-[state=open]:bg-secondary-foreground/10">
                  <div className="flex items-center space-x-2">
                    {section.icon && <span>{section.icon}</span>}
                    <span>
                      {section.label === "Highlight" ? (
                        <span className="text-primary">{section.label}</span>
                      ) : (
                        section.label
                      )}
                    </span>
                  </div>
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className={`p-4 bg-background shadow-lg border rounded-lg ${(section.label === "Community" || section.label === "Blog") ? "w-[600px] max-h-[80vh] overflow-y-auto" : "w-96"
                    }`}>
                    {section.description && (
                      <p className="text-sm text-muted-foreground mb-4 pb-4 border-b">
                        {section.description}
                      </p>
                    )}
                    <div className={`${(section.label === "Community" || section.label === "Blog") ? "grid grid-cols-2 gap-4" : "space-y-2"}`}>
                      {section.items.map((item) => (
                        <div key={item.href}>
                          {renderMenuItem(item, (section.label === "Community" || section.label === "Blog"))}
                        </div>
                      ))}
                    </div>

                    {/* Quick Search - Hide for large menus */}
                    {(section.label !== "Community" && section.label !== "Blog") && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                          <Input
                            placeholder="Quick search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </NavigationMenuContent>
              </>
            ) : (
              <NavigationMenuLink asChild>
                {section.href?.startsWith("http") ? (
                  <a
                    href={section.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-secondary-foreground/10 focus:bg-secondary-foreground/10"
                  >
                    {section.icon && <span className="mr-2">{section.icon}</span>}
                    {section.label}
                  </a>
                ) : (
                  <Link
                    to={section.href || "#"}
                    className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-secondary-foreground/10 focus:bg-secondary-foreground/10"
                  >
                    {section.icon && <span className="mr-2">{section.icon}</span>}
                    {section.label}
                  </Link>
                )}
              </NavigationMenuLink>
            )}
          </NavigationMenuItem>
        ))}

        {/* Employer Dashboard - only for employers/admins */}
        {isEmployer && (
          <NavigationMenuItem>
            <NavigationMenuLink asChild>
              <Link
                to="/employer-dashboard"
                className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-secondary-foreground/10 focus:bg-secondary-foreground/10"
              >
                Employer Dashboard
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        )}
        {/* AI Agent - for candidates */}
        {isAuthenticated && hasRole('CANDIDATE') && (
          <NavigationMenuItem>
            <NavigationMenuLink asChild>
              <Link
                to="/ai-agent"
                className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-secondary-foreground/10 focus:bg-secondary-foreground/10"
              >
                ✨ AI Agent
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        )}
        {/* Admin Dashboard - only for admins */}
        {hasRole('ADMIN') && (
          <NavigationMenuItem>
            <NavigationMenuLink asChild>
              <Link
                to="/admin-dashboard"
                className="group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-secondary-foreground/10 focus:bg-secondary-foreground/10"
              >
                Admin Dashboard
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

